// frontend/src/redux/slices/tradingSlice.js - Complete MetaApi Version
import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = 'https://gritwears.store/api';

const initialState = {
  tradingBot: {
    isRunning: false,
    config: {
      botToken: '',
      chatId: '',
      chatIds: [],
      enabled: false
    },
    loading: false
  },
  positions: [],
  trades: [],
  accountInfo: null,
  logs: [],
  quotes: [],
  news: []
};

const tradingSlice = createSlice({
  name: 'trading',
  initialState,
  reducers: {
    setTradingBotRunning: (state, action) => {
      state.tradingBot.isRunning = action.payload;
    },
    setTradingBotConfig: (state, action) => {
      state.tradingBot.config = { ...state.tradingBot.config, ...action.payload };
    },
    setTradingBotLoading: (state, action) => {
      state.tradingBot.loading = action.payload;
    },
    setPositions: (state, action) => {
      state.positions = action.payload;
    },
    addPosition: (state, action) => {
      state.positions.unshift(action.payload);
    },
    updatePosition: (state, action) => {
      const index = state.positions.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.positions[index] = action.payload;
      }
    },
    removePosition: (state, action) => {
      state.positions = state.positions.filter(p => p.id !== action.payload);
    },
    setTrades: (state, action) => {
      state.trades = action.payload;
    },
    addTrade: (state, action) => {
      state.trades.unshift(action.payload);
    },
    setAccountInfo: (state, action) => {
      state.accountInfo = action.payload;
    },
    setLogs: (state, action) => {
      state.logs = action.payload;
    },
    addLog: (state, action) => {
      state.logs.unshift(action.payload);
      if (state.logs.length > 500) state.logs.pop();
    },
    clearLogs: (state) => {
      state.logs = [];
    },
    setQuotes: (state, action) => {
      state.quotes = action.payload;
    },
    addQuoteToList: (state, action) => {
      state.quotes.push(action.payload);
    },
    updateQuoteInList: (state, action) => {
      const index = state.quotes.findIndex(q => q.id === action.payload.id);
      if (index !== -1) {
        state.quotes[index] = action.payload;
      }
    },
    removeQuoteFromList: (state, action) => {
      state.quotes = state.quotes.filter(q => q.id !== action.payload);
    },
    setNews: (state, action) => {
      state.news = action.payload;
    }
  }
});

export const {
  setTradingBotRunning,
  setTradingBotConfig,
  setTradingBotLoading,
  setPositions,
  addPosition,
  updatePosition,
  removePosition,
  setTrades,
  addTrade,
  setAccountInfo,
  setLogs,
  addLog,
  clearLogs,
  setQuotes,
  addQuoteToList,
  updateQuoteInList,
  removeQuoteFromList,
  setNews
} = tradingSlice.actions;

// ============================================
// TRADING BOT CONFIGURATION ACTIONS
// ============================================

// Load trading bot configuration from Firebase
export const loadTradingBotConfig = (userId) => async (dispatch) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/trading-bot/config/${userId}`);
    if (response.data) {
      dispatch(setTradingBotConfig(response.data));
    }
  } catch (error) {
    console.error('Error loading trading bot config:', error);
  }
};

// Save trading bot configuration to Firebase
export const saveTradingBotConfig = (userId, config) => async (dispatch) => {
  dispatch(setTradingBotLoading(true));
  try {
    const response = await axios.post(`${API_BASE_URL}/trading-bot/config`, {
      userId,
      ...config
    });
    if (response.data.success) {
      dispatch(setTradingBotConfig(config));
      toast.success('Trading bot configuration saved');
      return true;
    }
    toast.error('Failed to save configuration');
    return false;
  } catch (error) {
    toast.error('Error saving configuration');
    return false;
  } finally {
    dispatch(setTradingBotLoading(false));
  }
};

// ============================================
// TRADING BOT CONTROL ACTIONS (MetaApi Version)
// ============================================

// Start the trading bot (MetaApi version)
export const startTradingBot = (userId) => async (dispatch) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/trading-bot/start`, { userId });
    if (response.data.success) {
      dispatch(setTradingBotRunning(true));
      // If bot is already running, show appropriate message
      if (response.data.isRunning) {
        toast.success('Trading bot is already running');
      } else {
        toast.success('Trading bot started - monitoring your MetaTrader account');
      }
      return true;
    }
    toast.error(response.data.message || 'Failed to start trading bot');
    return false;
  } catch (error) {
    const errorMsg = error.response?.data?.message || 'Error starting trading bot';
    toast.error(errorMsg);
    return false;
  }
};

// Stop the trading bot
export const stopTradingBot = (userId) => async (dispatch) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/trading-bot/stop`, { userId });
    if (response.data.success) {
      dispatch(setTradingBotRunning(false));
      toast.success('Trading bot stopped');
      return true;
    }
    toast.error(response.data.message || 'Failed to stop trading bot');
    return false;
  } catch (error) {
    toast.error('Error stopping trading bot');
    return false;
  }
};

// Place a manual order (MetaApi version)
export const placeOrder = (userId, symbol, amount, type) => async (dispatch) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/trading-bot/order`, {
      userId,
      symbol,
      amount,
      type
    });
    
    if (response.data.success) {
      toast.success(`${type} order placed for ${symbol}`);
      // Refresh data after order
      dispatch(loadPositions(userId));
      dispatch(loadTrades(userId));
      return true;
    }
    toast.error(response.data.message || 'Order failed');
    return false;
  } catch (error) {
    console.error('Order error:', error);
    const errorMsg = error.response?.data?.message || error.message || 'Order failed';
    toast.error(errorMsg);
    return false;
  }
};

// Load account info
export const loadAccountInfo = (userId) => async (dispatch) => {
  try {
    // Account info comes via WebSocket, this is a placeholder
    // The actual account info will be received through socket events
  } catch (error) {
    console.error('Error loading account info:', error);
  }
};

// ============================================
// POSITION AND TRADE ACTIONS
// ============================================

// Load open positions
export const loadPositions = (userId) => async (dispatch) => {
  try {
    // Positions are received via WebSocket
    // This is just a placeholder - actual positions come from socket events
  } catch (error) {
    console.error('Error loading positions:', error);
  }
};

// Load trade history from Firebase
export const loadTrades = (userId, status = 'all') => async (dispatch) => {
    // No API call needed - trades are on MT5, not in our database
    dispatch(setTrades([]));
    return;
};

// ============================================
// LOG MANAGEMENT ACTIONS
// ============================================

// Load system logs from Firebase
export const loadLogs = (userId, filters = {}) => async (dispatch) => {
  try {
    const params = new URLSearchParams(filters);
    const response = await axios.get(`${API_BASE_URL}/logs/${userId}?${params}`);
    dispatch(setLogs(response.data));
  } catch (error) {
    console.error('Error loading logs:', error);
  }
};

// Delete a single log entry
export const deleteLog = (userId, logId) => async (dispatch) => {
  try {
    await axios.delete(`${API_BASE_URL}/logs/${userId}/${logId}`);
    dispatch(loadLogs(userId));
    toast.success('Log deleted');
    return true;
  } catch (error) {
    toast.error('Error deleting log');
    return false;
  }
};

// Delete all logs (optionally older than X days)
export const deleteAllLogs = (userId, olderThan = null) => async (dispatch) => {
  try {
    const url = olderThan 
      ? `${API_BASE_URL}/logs/${userId}?olderThan=${olderThan}`
      : `${API_BASE_URL}/logs/${userId}`;
    await axios.delete(url);
    dispatch(loadLogs(userId));
    toast.success('Logs deleted');
    return true;
  } catch (error) {
    toast.error('Error deleting logs');
    return false;
  }
};

// ============================================
// MOTIVATIONAL QUOTES ACTIONS
// ============================================

// Load all quotes for user
export const loadQuotes = (userId) => async (dispatch) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/quotes/${userId}`);
    dispatch(setQuotes(response.data));
  } catch (error) {
    console.error('Error loading quotes:', error);
  }
};

// Add a new quote
export const addQuote = (userId, text) => async (dispatch) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/quotes/${userId}`, { text });
    if (response.data.success) {
      dispatch(addQuoteToList(response.data.quote));
      toast.success('Quote added');
      return true;
    }
    return false;
  } catch (error) {
    toast.error('Error adding quote');
    return false;
  }
};

// Update an existing quote
export const updateQuote = (userId, quoteId, text) => async (dispatch) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/quotes/${userId}/${quoteId}`, { text });
    if (response.data.success) {
      dispatch(updateQuoteInList({ id: quoteId, text }));
      toast.success('Quote updated');
      return true;
    }
    return false;
  } catch (error) {
    toast.error('Error updating quote');
    return false;
  }
};

// Delete a quote
export const deleteQuote = (userId, quoteId) => async (dispatch) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/quotes/${userId}/${quoteId}`);
    if (response.data.success) {
      dispatch(removeQuoteFromList(quoteId));
      toast.success('Quote deleted');
      return true;
    }
    return false;
  } catch (error) {
    toast.error('Error deleting quote');
    return false;
  }
};

// ============================================
// ECONOMIC NEWS ACTIONS
// ============================================

// Load economic news
export const loadNews = (userId) => async (dispatch) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/news/${userId}`);
    dispatch(setNews(response.data));
  } catch (error) {
    console.error('Error loading news:', error);
  }
};

export default tradingSlice.reducer;